import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Sun, Wallet, Zap, FileText, ClipboardCheck,
  Wrench, Activity, ShieldCheck, Battery, Cable, Cpu, KeyRound,
  CheckCircle2, Phone, Calendar, Gauge
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const t = (lang, fr, en) => (lang === 'fr' ? fr : en);

const InfrastructuresEnergetiquesPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const heroImg =
    'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1920&q=70';

  const steps = [
    { n: '01', Icon: Activity, title: t(language, 'Audit énergétique', 'Energy audit'),
      text: t(language,
        "Relevé des consommations existantes (factures SNEL, groupe électrogène), profil de charge, identification des postes énergivores et du potentiel d'économies.",
        'Survey of existing consumption (SNEL bills, generator), load profile, identification of energy-intensive uses and savings potential.') },
    { n: '02', Icon: Gauge, title: t(language, 'Dimensionnement', 'Sizing'),
      text: t(language,
        "Calcul de la puissance nécessaire, du productible solaire (5,2–5,8 kWh/m²/jour à Kinshasa), du stockage batterie et de la part SNEL ou groupe en hybride.",
        'Calculation of required power, solar yield (5.2–5.8 kWh/m²/day in Kinshasa), battery storage and SNEL or generator share in a hybrid setup.') },
    { n: '03', Icon: FileText, title: t(language, 'Études techniques', 'Technical studies'),
      text: t(language,
        "Schéma unifilaire, plans d'implantation, calculs de chute de tension, plan de mise à la terre, parafoudre, choix des onduleurs (string, hybride ou centralisé).",
        'Single-line diagram, layout drawings, voltage-drop calculations, earthing plan, surge protection, inverter choice (string, hybrid or central).') },
    { n: '04', Icon: ShieldCheck, title: t(language, 'Autorisations & conformité', 'Approvals & compliance'),
      text: t(language,
        "Déclaration auprès de l'ARE (Autorité de Régulation du secteur de l'Électricité), conformité aux normes IEC, certifications panneaux Tier 1 et onduleurs CE/UL.",
        'Filing with ARE (Electricity Regulatory Authority), compliance with IEC standards, Tier 1 panels and CE/UL certified inverters.') },
    { n: '05', Icon: Cable, title: t(language, 'Approvisionnement', 'Procurement'),
      text: t(language,
        "Importation des équipements (Asie/Europe), dédouanement, transport jusqu'au site, contrôle qualité à réception et stockage sécurisé avant pose.",
        'Equipment import (Asia/Europe), customs clearance, transport to site, quality control on reception and secure storage before installation.') },
    { n: '06', Icon: Wrench, title: t(language, 'Installation', 'Installation'),
      text: t(language,
        "Pose des structures (toiture, ombrière, parc au sol), câblage CC/CA, raccordement onduleurs et batteries Lithium-Ion, intégration au tableau général basse tension.",
        'Mounting structures (roof, carport, ground array), DC/AC cabling, inverter and Li-Ion battery wiring, integration into the main LV panel.') },
    { n: '07', Icon: Zap, title: t(language, 'Mise en service', 'Commissioning'),
      text: t(language,
        "Tests électriques, paramétrage des onduleurs et de l'EMS (Energy Management System), couplage SNEL/groupe/solaire/batterie en mode hybride sans coupure.",
        'Electrical tests, inverter and EMS (Energy Management System) configuration, SNEL/generator/solar/battery coupling in seamless hybrid mode.') },
    { n: '08', Icon: KeyRound, title: t(language, 'Suivi & maintenance', 'Monitoring & maintenance'),
      text: t(language,
        "Plateforme de monitoring 24/7, alertes en temps réel, contrats de maintenance préventive (lavage panneaux, contrôle batteries, mise à jour firmware).",
        '24/7 monitoring platform, real-time alerts, preventive maintenance contracts (panel cleaning, battery checks, firmware updates).') },
  ];

  const prepBlocks = [
    { Icon: Wallet, title: t(language, 'Budget & retour sur investissement', 'Budget & ROI'),
      points: [
        t(language, "Évaluer le coût au kWc installé selon technologie (solaire, hybride, off-grid).", 'Assess cost per installed kWp depending on technology (solar, hybrid, off-grid).'),
        t(language, "Calculer le retour sur investissement vs. coût SNEL + carburant groupe.", 'Calculate ROI vs. SNEL cost + generator fuel.'),
        t(language, "Solutions de financement : leasing, crédit-bail, PPA (Power Purchase Agreement).", 'Financing solutions: leasing, financial lease, PPA (Power Purchase Agreement).'),
        t(language, "Anticiper le coût de remplacement des batteries à 8–10 ans.", 'Anticipate battery replacement cost at 8–10 years.'),
      ] },
    { Icon: Activity, title: t(language, 'Diagnostic préalable', 'Prior diagnostic'),
      points: [
        t(language, "Bilan de consommation 12 mois (factures, journaux groupe).", '12-month consumption review (bills, generator logs).'),
        t(language, "Mesure des charges critiques et non critiques.", 'Measurement of critical and non-critical loads.'),
        t(language, "Étude d'ombrage et orientation toiture (plein sud à Kinshasa).", 'Shading study and roof orientation (due south in Kinshasa).'),
        t(language, "Analyse de la qualité du réseau SNEL (variations, micro-coupures).", 'Analysis of SNEL grid quality (fluctuations, micro-outages).'),
      ] },
    { Icon: ShieldCheck, title: t(language, 'Cadre réglementaire', 'Regulatory framework'),
      points: [
        t(language, "Loi n°14/011 sur le secteur de l'électricité (libéralisation 2014).", 'Law No. 14/011 on the electricity sector (2014 liberalisation).'),
        t(language, "Autorisation de l'ARE pour les installations >50 kW.", 'ARE authorisation for installations above 50 kW.'),
        t(language, "Conformité aux normes IEC 61215, 61730, 62109 pour les équipements.", 'Compliance with IEC 61215, 61730, 62109 standards.'),
        t(language, "Convention avec SNEL en cas de raccordement bidirectionnel.", 'Agreement with SNEL in case of bidirectional connection.'),
      ] },
    { Icon: Cpu, title: t(language, 'Choix de l\u2019installateur', 'Choosing the installer'),
      points: [
        t(language, "Vérifier les références d'installations >100 kWc à Kinshasa.", 'Check references for installations >100 kWp in Kinshasa.'),
        t(language, "Équipe d'ingénieurs électriciens et techniciens habilités.", 'Team of qualified electrical engineers and technicians.'),
        t(language, "Garantie produit (12–25 ans panneaux), performance et de pose.", 'Product warranty (12–25 years panels), performance and installation.'),
        t(language, "Service après-vente avec stock pièces détachées local.", 'After-sales service with local spare parts stock.'),
      ] },
  ];

  const rdcSpecifics = [
    { Icon: Sun, title: t(language, 'Ressource solaire', 'Solar resource'),
      text: t(language,
        "Kinshasa bénéficie d'un ensoleillement annuel d'environ 1 900 kWh/m². Une installation photovoltaïque y produit en moyenne 1 400 à 1 600 kWh par kWc installé chaque année.",
        'Kinshasa enjoys annual irradiance of around 1,900 kWh/m². A PV installation typically produces 1,400 to 1,600 kWh per installed kWp annually.') },
    { Icon: Zap, title: t(language, 'Réseau SNEL', 'SNEL grid'),
      text: t(language,
        "Le réseau de distribution connaît des variations de tension et des micro-coupures fréquentes. Onduleurs hybrides et stabilisateurs sont systématiquement intégrés pour protéger le matériel sensible.",
        'The distribution grid has frequent voltage variations and micro-outages. Hybrid inverters and stabilisers are systematically integrated to protect sensitive equipment.') },
    { Icon: Battery, title: t(language, 'Stockage batterie', 'Battery storage'),
      text: t(language,
        "Les batteries Lithium-Fer-Phosphate (LiFePO4) sont privilégiées : 6 000+ cycles, sécurité thermique, fonctionnement entre 0 et 50 °C, idéales pour le climat tropical humide.",
        'Lithium Iron Phosphate (LiFePO4) batteries are preferred: 6,000+ cycles, thermal safety, operation between 0 and 50 °C, ideal for the humid tropical climate.') },
    { Icon: Wrench, title: t(language, 'Maintenance locale', 'Local maintenance'),
      text: t(language,
        "Lavage des panneaux toutes les 4 à 6 semaines en saison sèche (poussière harmattan), inspection thermique annuelle, mise à jour firmware et calibration mensuelle de l'EMS.",
        'Panel cleaning every 4 to 6 weeks in the dry season (harmattan dust), annual thermal inspection, firmware updates and monthly EMS calibration.') },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[240px]" data-testid="energy-infra-page">
        <section className="relative h-[360px] md:h-[460px] overflow-hidden" data-testid="energy-hero">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImg})` }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />
          <div className="relative max-w-6xl mx-auto px-6 h-full flex flex-col justify-center">
            <button onClick={() => navigate('/')} className="self-start inline-flex items-center gap-2 text-white/85 hover:text-orange-400 mb-6 transition-colors" data-testid="energy-back-btn">
              <ArrowLeft size={18} />
              <span className="font-semibold">{t(language, "Retour à l'accueil", 'Back to home')}</span>
            </button>
            <h1 className="text-white text-3xl sm:text-4xl md:text-6xl font-bold leading-tight max-w-3xl">
              {t(language, "Infrastructures énergétiques en RDC", 'Energy infrastructure in DRC')}
            </h1>
            <p className="text-white/85 mt-4 max-w-2xl text-base md:text-lg">
              {t(language,
                "Solaire, hybride, mini-réseaux : sécurisez votre alimentation et réduisez durablement votre facture.",
                'Solar, hybrid, mini-grids: secure your power supply and reduce your bill sustainably.')}
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20" data-testid="energy-intro">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-6" style={{ color: '#702963' }}>
              {t(language,
                "Une énergie fiable, c'est la condition d'une activité durable",
                'Reliable energy is the condition for sustainable activity')}
            </h2>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              {t(language,
                "Industries, hôpitaux, hôtels, immeubles tertiaires, télécoms et logements collectifs : tous dépendent d'une énergie stable. À Kinshasa, où le réseau SNEL connaît des fluctuations et où le carburant pèse lourd dans les charges, une infrastructure solaire ou hybride bien dimensionnée garantit la continuité d'exploitation et un retour sur investissement rapide.",
                'Industry, hospitals, hotels, office buildings, telecoms and residential complexes all depend on stable power. In Kinshasa, where the SNEL grid fluctuates and fuel weighs heavily on operating costs, a properly sized solar or hybrid infrastructure ensures business continuity and a fast return on investment.')}
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-gray-50" data-testid="energy-prep">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, "Préparation client : 4 piliers", 'Client preparation: 4 pillars')}
              </h2>
              <div className="mx-auto h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {prepBlocks.map((b, idx) => {
                const Icon = b.Icon;
                return (
                  <div key={idx} className="bg-white border border-gray-200 rounded-sm p-7 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300" data-testid={`energy-prep-${idx}`}>
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-14 h-14 flex items-center justify-center bg-orange-50 rounded-full text-orange-600 flex-shrink-0">
                        <Icon className="w-7 h-7" strokeWidth={2} />
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900">{b.title}</h3>
                    </div>
                    <ul className="space-y-3">
                      {b.points.map((p, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-700 text-sm md:text-base leading-relaxed">
                          <CheckCircle2 className="w-5 h-5 text-[#702963] flex-shrink-0 mt-0.5" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24" data-testid="energy-steps">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, "Les 8 étapes d'un projet énergétique", 'The 8 steps of an energy project')}
              </h2>
              <div className="mx-auto mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>
            <div className="relative">
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2" />
              <div className="space-y-10 md:space-y-16">
                {steps.map((s, i) => {
                  const Icon = s.Icon;
                  const left = i % 2 === 0;
                  return (
                    <div key={s.n} className="md:grid md:grid-cols-2 md:gap-12 items-center" data-testid={`energy-step-${s.n}`}>
                      <div className={`${left ? 'md:order-1 md:text-right' : 'md:order-2'} mb-4 md:mb-0`}>
                        <div className={`flex items-start gap-4 ${left ? 'md:flex-row-reverse md:text-right' : ''}`}>
                          <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#702963] text-white rounded-full flex-shrink-0">
                            <Icon className="w-6 h-6 md:w-7 md:h-7" strokeWidth={2} />
                          </div>
                          <div>
                            <span className="text-orange-500 font-bold text-sm tracking-widest">{t(language, 'ÉTAPE', 'STEP')} {s.n}</span>
                            <h3 className="text-lg md:text-2xl font-bold text-gray-900 mt-1 mb-2">{s.title}</h3>
                            <p className="text-gray-700 text-sm md:text-base leading-relaxed">{s.text}</p>
                          </div>
                        </div>
                      </div>
                      <div className={`hidden md:block ${left ? 'md:order-2' : 'md:order-1'}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-gray-50" data-testid="energy-rdc">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, "Spécificités RDC", 'DRC specifics')}
              </h2>
              <div className="mx-auto mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {rdcSpecifics.map((r, i) => {
                const Icon = r.Icon;
                return (
                  <div key={i} className="bg-white p-6 md:p-7 border-t-4 border-[#702963] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`energy-rdc-card-${i}`}>
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-full text-orange-600 mb-4">
                      <Icon className="w-6 h-6" strokeWidth={2} />
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">{r.title}</h3>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">{r.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20" data-testid="energy-duration">
          <div className="max-w-5xl mx-auto px-6">
            <div className="bg-[#702963] text-white p-8 md:p-12 rounded-sm shadow-2xl">
              <div className="flex items-start gap-5">
                <Calendar className="w-10 h-10 text-orange-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl md:text-3xl font-bold mb-3">{t(language, 'Durée indicative', 'Indicative duration')}</h3>
                  <p className="text-white/90 text-base md:text-lg leading-relaxed">
                    {t(language,
                      "Une centrale solaire toiture de 30 à 100 kWc se déploie en 8 à 14 semaines, autorisations comprises. Pour un système hybride industriel de 200 à 500 kWc avec stockage, comptez 5 à 8 mois entre l'audit et la mise en service.",
                      'A 30 to 100 kWp rooftop solar plant takes 8 to 14 weeks including approvals. For a 200 to 500 kWp industrial hybrid system with storage, allow 5 to 8 months from audit to commissioning.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white" data-testid="energy-cta">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-4" style={{ color: '#702963' }}>
              {t(language, 'Réduisez votre facture énergétique', 'Cut your energy bill')}
            </h2>
            <p className="text-gray-700 text-base md:text-lg mb-8 max-w-2xl mx-auto">
              {t(language,
                "Audit gratuit et étude de dimensionnement chiffrée sous 14 jours.",
                'Free audit and costed sizing study within 14 days.')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => navigate('/votre-projet')} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-sm transition-all duration-300 transform hover:scale-105" data-testid="energy-cta-project">
                {t(language, 'Demander un audit', 'Request an audit')}
                <ArrowRight size={18} />
              </button>
              <a href="tel:+243971327318" className="inline-flex items-center gap-2 border-2 border-[#702963] text-[#702963] font-semibold px-8 py-3 hover:bg-[#702963] hover:text-white transition-all" data-testid="energy-cta-call">
                <Phone size={18} />
                {t(language, 'Appelez-nous', 'Call us')}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default InfrastructuresEnergetiquesPage;
